import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { writingAPI } from '../../api/client'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

const PROMPTS = [
  { id: 1, label: 'Task 2 — Opinion', text: 'Some people believe that technology has made our lives more complicated rather than simpler. To what extent do you agree or disagree?' },
  { id: 2, label: 'Task 2 — Discussion', text: 'Some people think that children should begin their formal education at a very early age. Others believe that children should not go to school until they are older. Discuss both views and give your opinion.' },
  { id: 3, label: 'Task 1 — Report', text: 'The chart below shows the percentage of households in owned and rented accommodation in England and Wales between 1918 and 2011. Summarise the information by selecting and reporting the main features.' },
]

export default function WritingRewriteComparisonPage() {
  const [promptIdx, setPromptIdx] = useState(0)
  const [draft, setDraft] = useState('')
  const [result, setResult] = useState<any>(null)
  const [showDiff, setShowDiff] = useState(false)

  const prompt = PROMPTS[promptIdx]

  const submit = useMutation({
    mutationFn: () => writingAPI.submit({ taskType: 'task2', taskPrompt: prompt.text, text: draft, targetLevel: 'B2', sessionId: 'rewrite-comparison' }),
    onSuccess: setResult,
  })

  function reset() { setDraft(''); setResult(null); setShowDiff(false) }

  // Simple diff: highlight changed words
  function renderDiff(original: string, rewritten: string) {
    const origWords = original.split(' ')
    const newWords = rewritten.split(' ')
    return newWords.map((word, i) => {
      const changed = origWords[i] !== word
      return (
        <span key={i} style={{ background: changed ? 'rgba(0,75,226,0.12)' : 'transparent', borderRadius: '0.25rem', padding: changed ? '0 0.125rem' : '0', color: changed ? '#004be2' : 'inherit', fontWeight: changed ? 700 : 400 }}>
          {word}{' '}
        </span>
      )
    })
  }

  return (
    <AppLayout>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>compare_arrows</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8e00d5' }}>Writing — Rewrite Comparison</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          So sánh bài viết <span style={{ color: '#004be2', fontStyle: 'italic' }}>trước & sau</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          Viết bài, AI chấm điểm và viết lại phiên bản cải thiện. Bạn thấy ngay sự khác biệt.
        </p>
      </div>

      {/* Prompt selector */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {PROMPTS.map((p, i) => (
          <button key={p.id} onClick={() => { setPromptIdx(i); reset() }}
            style={{ padding: '0.5rem 1.25rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.8125rem', background: promptIdx === i ? '#8e00d5' : '#eef0ff', color: promptIdx === i ? 'white' : '#535b71' }}>
            {p.label}
          </button>
        ))}
      </div>

      {!result ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
          {/* Prompt */}
          <div style={{ background: 'linear-gradient(135deg, #8e00d5, #b041ff)', borderRadius: '1.5rem', padding: '2rem' }}>
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.75rem' }}>Đề bài</span>
            <p style={{ color: 'white', fontSize: '1rem', lineHeight: 1.7, fontWeight: 500 }}>{prompt.text}</p>
          </div>

          {/* Editor */}
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <H style={{ fontWeight: 800, fontSize: '1rem' }}>Bài viết của bạn</H>
              <span style={{ fontSize: '0.8125rem', color: draft.split(/\s+/).filter(Boolean).length >= 250 ? '#004be2' : '#535b71', fontWeight: 700 }}>
                {draft.split(/\s+/).filter(Boolean).length} từ {prompt.label.includes('Task 2') ? '(tối thiểu 250)' : '(tối thiểu 150)'}
              </span>
            </div>
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="Viết bài của bạn tại đây..."
              rows={14}
              style={{ width: '100%', padding: '1rem', borderRadius: '1rem', border: '1.5px solid #d1dcff', background: '#f6f6ff', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', lineHeight: 1.75, resize: 'vertical', outline: 'none', boxSizing: 'border-box', color: '#272e42' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                onClick={() => submit.mutate()}
                disabled={draft.split(/\s+/).filter(Boolean).length < 50 || submit.isPending}
                style={{ padding: '1rem 2.5rem', borderRadius: '9999px', border: 'none', cursor: draft.length < 50 ? 'not-allowed' : 'pointer', background: draft.split(/\s+/).filter(Boolean).length < 50 ? 'rgba(142,0,213,0.3)' : 'linear-gradient(135deg, #8e00d5, #b041ff)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '1rem', boxShadow: '0 8px 24px rgba(142,0,213,0.25)', display: 'inline-flex', alignItems: 'center', gap: '0.625rem' }}>
                {submit.isPending ? <><div className="spinner" style={{ width: '1rem', height: '1rem', borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Đang chấm...</> : <><span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>auto_fix_high</span> Chấm & Viết lại</>}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Score bar */}
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(142,0,213,0.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <H style={{ fontSize: '1.25rem', fontWeight: 800 }}>Kết quả chấm điểm</H>
              <button onClick={reset}
                style={{ padding: '0.625rem 1.25rem', borderRadius: '9999px', border: '2px solid #d1dcff', background: 'transparent', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }}>
                Viết lại
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
              {[
                { label: 'Tổng điểm', value: result.scoreOverall, color: '#8e00d5' },
                { label: 'Task Achievement', value: result.scoreTaskAchievement, color: '#004be2' },
                { label: 'Coherence', value: result.scoreCoherence, color: '#006571' },
                { label: 'Vocabulary', value: result.scoreVocabulary, color: '#b45309' },
                { label: 'Grammar', value: result.scoreGrammar, color: '#b41340' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center', padding: '1.25rem', background: `${s.color}08`, borderRadius: '1rem' }}>
                  <H style={{ display: 'block', fontSize: '2rem', fontWeight: 900, color: s.color }}>{s.value?.toFixed(1) ?? '—'}</H>
                  <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Toggle diff */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={() => setShowDiff(false)}
              style={{ padding: '0.625rem 1.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.875rem', background: !showDiff ? '#004be2' : '#eef0ff', color: !showDiff ? 'white' : '#535b71' }}>
              So sánh song song
            </button>
            <button onClick={() => setShowDiff(true)}
              style={{ padding: '0.625rem 1.5rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: '0.875rem', background: showDiff ? '#004be2' : '#eef0ff', color: showDiff ? 'white' : '#535b71' }}>
              Highlight thay đổi
            </button>
          </div>

          {/* Comparison */}
          {!showDiff ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
                  <div style={{ width: '0.75rem', height: '0.75rem', borderRadius: '50%', background: '#b41340' }} />
                  <H style={{ fontWeight: 800, fontSize: '0.9375rem' }}>Bài viết gốc</H>
                </div>
                <p style={{ fontSize: '0.9375rem', lineHeight: 1.85, color: '#272e42' }}>{draft}</p>
              </div>
              <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 8px 24px rgba(0,75,226,0.08)', border: '2px solid #d1dcff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
                  <div style={{ width: '0.75rem', height: '0.75rem', borderRadius: '50%', background: '#004be2' }} />
                  <H style={{ fontWeight: 800, fontSize: '0.9375rem', color: '#004be2' }}>Phiên bản AI cải thiện</H>
                </div>
                <p style={{ fontSize: '0.9375rem', lineHeight: 1.85, color: '#272e42' }}>{result.improvedVersion ?? result.feedback}</p>
              </div>
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
              <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', marginBottom: '1.25rem' }}>
                Highlight thay đổi <span style={{ background: 'rgba(0,75,226,0.12)', color: '#004be2', padding: '0.125rem 0.5rem', borderRadius: '0.375rem', fontSize: '0.875rem' }}>màu xanh = thay đổi</span>
              </H>
              <p style={{ fontSize: '0.9375rem', lineHeight: 1.85, color: '#272e42' }}>
                {renderDiff(draft, result.improvedVersion ?? result.feedback ?? '')}
              </p>
            </div>
          )}

          {/* Feedback */}
          {result.feedback && (
            <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>psychology</span>
                <H style={{ fontWeight: 800, fontSize: '1rem', color: '#8e00d5' }}>Nhận xét chi tiết</H>
              </div>
              <p style={{ fontSize: '0.9375rem', lineHeight: 1.75, color: '#272e42' }}>{result.feedback}</p>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  )
}
